<template>
  <article v-if="show" class="show-full">
    <div class="show-full__nav">
      <BackButton to="/radio/sendungen" />
      <BackendLink :path />
    </div>
    <PageHeading :title="show.title" class="show-full__heading" />
    <div class="show-full__content">
      <div v-if="show.image" class="show-full__cover">
        <ResponsiveImage :image="show.image" />
      </div>
      <div class="show-full__info">
        <div v-if="show.fieldText" class="show-full__text" v-html="useSanitizeHtml(show.fieldText)" />
        <div class="show-full__links-list">
          <IconLink :url="{ path: '/radio/feedback', query: { show: encodeURI(show.title) }}" :label="`Feedback Formular`" icon="CircleArrowRightFilled" />
          <IconLink v-for="(link) in show.links" :key="`show-full__link--${link.uuid}`" class="show-full__link" :label="link.title" :url="link.uri.path" />
        </div>
        <div v-if="show.tags.length" class="show-full__tags-list">
          <div v-for="(tag) in show.tags" :key="`show-full__tag--${tag.uuid}`" class="show-full__tag">
            {{ tag.label }}
          </div>
        </div>
      </div>
    </div>
    <div class="show-full__body">
      <div v-if="show.upcomingEpisodes.query.items.length" class="show-full__episodes">
        <h2 class="show-full__episodes-heading">
          Nächste Sendungsausgaben
        </h2>
        <div class="show-full__episodes-list">
          <EpisodesTable :episodes="show.upcomingEpisodes.query.items" :library="false" />
        </div>
      </div>
      <div v-if="show.pastEpisodes.query.items.length" class="show-full__episodes">
        <h2 class="show-full__episodes-heading">
          Vergangene Sendungsausgaben
        </h2>
        <div class="show-full__episodes-list">
          <EpisodesTable :episodes="show.pastEpisodes.query.items.slice(0, 20)" :library="true" />
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { startOfDay } from 'date-fns'
import type { UseSeoMetaInput } from '@unhead/schema'
import { type ShowFullFragment, type ShowQuery } from '#graphql-operations'

const props = defineProps<{
  path: string
}>()

const today = Date.now()

// set min and max date range to filter to (3 days range)
const dateRangeStart = useFormatDateGraphqlFilter(startOfDay(today))

// @todo: Switching to useAsyncGraphqlQuery breaks SSR...
const { data } = await useAsyncData('show', () => {
  return useGraphqlQuery('show', { path: props.path, dateRangeStart }).then((v) => {
    const data: ShowQuery = v.data
    return data
  })
})
/*
const { data } = await useAsyncGraphqlQuery('show', { path: props.path, dateRangeStart }, {
  transform: function (v) {
    return v.data as ShowQuery
  }
})
*/

const route: EntityRoute | undefined | null = data.value?.route

await useHandleRedirect(route)

if (route?.entity === undefined) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

const show: ShowFullFragment = route.entity

const description = useStripHTML(show.fieldText ?? '')
const ogImageUrl = useImageFallback(show?.image)

const metatags: UseSeoMetaInput = {
  title: show.title,
  description,
  ogDescription: description
}

if (ogImageUrl.length > 0) {
  metatags.ogImageUrl = ogImageUrl
}

useSeoMeta(metatags)

</script>

<style lang="scss">
@use "~/assets/sass/tools";

  .show-full {
    &__nav {
      display: flex;
      justify-content: space-between;
      margin-bottom: var(--space--l);
    }

    &__content {
      @include tools.grid;
    }

    &__cover {
      grid-column: span 2;
      @include tools.to(tools.$breakpoint--desktop) {
        grid-column: span 6;
      }
    }

    &__info {
      grid-column: span 4;
      display: flex;
      flex-direction: column;
      gap: var(--grid-gutter);
      @include tools.to(tools.$breakpoint--desktop) {
        grid-column: span 6;
      }
    }

    &__text {
      p {
        &:first-child {
          margin-top: 0;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__links-list {
      display: flex;
      flex-direction: column;
      gap: var(--space--s);
    }

    &__tags-list {
      display: flex;
      gap: var(--space--s);
      flex-wrap: wrap;
    }

    &__tag {
      @include tools.tag-big;
    }

    &__body {
      @include tools.grid;
      row-gap: var(--space--xl);
      margin: var(--space--xxl) 0;
    }

    &__episodes {
      @include tools.page-grid-columns;

    }

    &__episodes-heading {
      margin: 0;
      margin-bottom: var(--grid-gutter);
    }
  }
</style>
